import { ReferenceSchema } from '@framework/engine/schema/expressions/validation/ReferenceSchema.ts';
import { BaseLayoutSchema } from '@framework/engine/schema/widgets/repository/common/BaseLayoutSchema.ts';
import { CommonInputWidgetConfigSchema } from '@framework/engine/schema/widgets/repository/inputs/common/CommonInputConfigComponent.tsx';
import { yup } from '@framework/validation/initYup.ts';
import { MapPrintImageSchema } from './config/print/MapPrintImageSchema.tsx';
import {
  type IMapWidget,
  MapWidgetSelectionShape,
  WidgetTypeMap,
} from './IMapWidget.ts';
import { MapGroupSchema } from './layers/MapGroup.ts';
import { MapLayerType } from './layers/MapLayer.ts';
import { MapLayerSchema } from './layers/schema/MapLayerSchema.ts';
import {
  createSingleCadastralPresetTest,
  createUniqueLayerCodesTest,
} from './layers/schema/schemaValidationUtils.ts';

export const MapWidgetConfigSchema: yup.ObjectSchema<IMapWidget> =
  CommonInputWidgetConfigSchema.shape({
    type: yup.string().exact(WidgetTypeMap).required(),
    layout: BaseLayoutSchema.shape({}),
    position: yup.object({
      center: yup
        .tuple([yup.number().required(), yup.number().required()])
        .default([11.189_417_912_530_622, 44.576_276_442_981_985]),
      zoom: yup.number().default(4),
    }),
    layers: yup
      .array(MapLayerSchema)
      .default([])
      .test(createUniqueLayerCodesTest())
      .test(createSingleCadastralPresetTest())
      .test(
        'max-one-primary-competence',
        'Solo un layer può avere competenza primaria abilitata',
        layers => {
          const primaryCount =
            layers?.filter(
              layer =>
                layer.competence?.enabled &&
                layer.competence.role === 'primary',
            ).length ?? 0;
          return primaryCount <= 1;
        },
      ),
    groups: yup
      .array(MapGroupSchema)
      .default([])
      .test(
        'unique-group-names',
        'I nomi dei gruppi devono essere univoci',
        groups => {
          const names = groups?.map(group => group.name).filter(Boolean) ?? [];
          return names.length === new Set(names).size;
        },
      ),
    bufferDistance: yup
      .number()
      .moreThan(0)
      .default(5)
      .label('Distanza di buffer (m)'),
    printImages: yup.array(MapPrintImageSchema).default([]),
    selection: yup.object({
      multiple: yup.boolean().default(true).label('Selezione multipla'),
      shapes: yup
        .array()
        .of(yup.string().oneOfEnum(MapWidgetSelectionShape).required())
        .min(1)
        .default([MapWidgetSelectionShape.Polygon])
        .label('Geometrie Selezionabili')
        .required(),
      hideGeometriesTable: yup
        .boolean()
        .default(false)
        .label('Nascondi tabella geometrie'),
    }),
    competence: yup
      .object({
        // La competenza interseca le geometrie disegnate con i layer di
        // competenza (criterio della massima area di sovrapposizione).
        // Punti e linee vengono bufferizzati in poligoni (vedi
        // getBufferedFeatureJSON), quindi il riconoscimento è disponibile
        // per tutte le tipologie di geometria.
        enabled: yup
          .boolean()
          .default(false)
          .label('Abilita riconoscimento Competenza'),
        type: yup
          .string()
          .label('Tipo')
          .oneOfEnum(MapLayerType)
          .when(['enabled'], ([enabled], schema) =>
            enabled ? schema.required() : schema.strip(),
          ),
        url: yup
          .string()
          .label('URL')
          .when(['enabled'], ([enabled], schema) =>
            enabled ? schema.required() : schema.strip(),
          ),
        propertyName: yup
          .string()
          .label('Nome proprietà per Identificare le Competenze')
          .when(['enabled'], ([enabled], schema) =>
            enabled ? schema.required() : schema.strip(),
          ),
      })
      .addChildrenParentDeps(),
    intersection: yup
      .object({
        // L'intersezione bufferizza punti e linee in poligoni
        // (vedi getBufferedFeatureJSON), quindi è disponibile per tutte
        // le tipologie di geometria, non solo per i poligoni.
        enabled: yup.boolean().default(false).label('Intersezione'),
        title: yup
          .string()
          .default('Intersezioni')
          .label('Titolo Intersezioni')
          .when(['enabled'], ([enabled], schema) =>
            enabled ? schema.required() : schema.strip(),
          ),
        type: yup
          .string()
          .label('Tipo')
          .oneOfEnum(MapLayerType)
          .when(['enabled'], ([enabled], schema) =>
            enabled ? schema.required() : schema.strip(),
          ),
        url: yup
          .string()
          .label('URL')
          .when(['enabled'], ([enabled], schema) =>
            enabled ? schema.required() : schema.strip(),
          ),
        areaTolerance: yup
          .number()
          .default(0.05)
          .label('Tolleranza (metri quadri minimi)')
          .when(['enabled'], ([enabled], schema) =>
            enabled ? schema.required() : schema.strip(),
          ),
      })
      .addChildrenParentDeps(),
    identify: yup
      .object({
        bufferDistance: yup
          .number()
          .min(1)
          .default(10)
          .label('Buffer Identify (m)'),
        bufferDistanceBO: yup
          .number()
          .min(1)
          .default(10)
          .label('Buffer Identify BO (m)'),
      })
      .default({}),
    labeling: yup
      .object({
        enabled: yup.boolean().default(true).label('Abilita etichette'),
        zoomRange: yup
          .object({
            min: yup.number().min(0).max(22).default(0).label('Zoom minimo'),
            max: yup.number().min(0).max(22).default(22).label('Zoom massimo'),
          })
          .test(
            'zoom-range-valid',
            'Lo zoom minimo deve essere minore o uguale allo zoom massimo',
            range => {
              if (range?.min == null || range?.max == null) return true;
              return range.min <= range.max;
            },
          )
          .default({}),
        prefix: yup.string().default('#').label('Prefisso etichetta'),
      })
      .default({}),
    municipalityGroupList: yup
      .array()
      .of(yup.string().required())
      .label('Gruppo Comuni'),
    defaultValueReference:
      ReferenceSchema.notRequired().label('Valore di Default'),
  })
    .required()
    .noUnknown();
